import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type ReactElement } from 'react';
import { useSessionSummary } from '@/lib/queries/practice-session-summary';
import { useUpdatePracticeSession } from '@/lib/queries/use-update-practice-session';
import { heroCopyForSession } from '@/lib/practice/hero-copy';
import { MoodPicker } from '@/components/molecules/mood-picker';
import { StatTile } from '@/components/molecules/stat-tile';
import { Card } from '@/components/atoms/card';
import { Button } from '@/components/atoms/button';
import { Micro } from '@/components/atoms/micro';
import { cn } from '@/lib/cn';
import type { Mood } from '@/lib/types';

export const Route = createFileRoute('/practice_/sessions_/$sessionId_/summary')({
  component: SessionSummaryPage,
});

function SessionSummaryPage(): ReactElement {
  const { sessionId } = Route.useParams();
  const navigate = useNavigate();
  const { data: summary, isLoading, isError } = useSessionSummary(sessionId);
  const updateSession = useUpdatePracticeSession();

  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [moodInitialised, setMoodInitialised] = useState(false);
  const [notesInitialised, setNotesInitialised] = useState(false);

  if (summary && !moodInitialised) {
    setMoodInitialised(true);
    setSelectedMood(summary.mood ?? null);
  }

  if (summary && !notesInitialised) {
    setNotesInitialised(true);
    setNotes(summary.notes ?? '');
  }

  const heroCopy = useMemo(() => {
    if (!summary) return '';
    return heroCopyForSession(sessionId, {
      durationMinutes: summary.durationMinutes ?? 0,
      streakDays: summary.streakDays,
      tunes: summary.tunesPracticed,
    });
  }, [sessionId, summary]);

  const handleMoodSelect = (mood: Mood | null): void => {
    setSelectedMood(mood);
    updateSession.mutate({ sessionId, mood });
  };

  const handleNotesBlur = (): void => {
    updateSession.mutate({ sessionId, notes });
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-page)] text-sm text-[var(--ink-3)]">
        Loading…
      </div>
    );
  }

  if (isError || !summary) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg-page)] p-6 text-center">
        <p className="font-display text-xl font-semibold text-[var(--ink)]">Session not found</p>
        <p className="text-sm text-[var(--ink-3)]">This session may have been removed.</p>
        <Link to="/practice">
          <Button variant="secondary" size="sm">
            Back to practice
          </Button>
        </Link>
      </div>
    );
  }

  const tunesCount = summary.tunesPracticed;

  return (
    <div className="min-h-screen bg-[var(--bg-page)] pb-24 text-[var(--ink)]">
      <header className="border-b border-[var(--rule)] bg-[var(--bg-surface)] px-5 md:px-8 pt-6 pb-6">
        <div className="mx-auto max-w-3xl">
          <h1 className="font-display text-[28px] md:text-[36px] font-semibold tracking-tight leading-[1.1] text-[var(--ink)]">
            {heroCopy}
          </h1>
        </div>
      </header>

      <main className="mx-auto max-w-3xl flex flex-col gap-6 px-5 py-8 md:px-8">
        <section aria-label="Session stats">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatTile
              label="Duration"
              value={String(summary.durationMinutes ?? 0)}
              unit="min"
              icon="clock"
              tone="brand"
            />
            <StatTile
              label="Tunes"
              value={String(tunesCount)}
              icon="music"
              tone="neutral"
            />
            <StatTile
              label="Streak"
              value={String(summary.streakDays)}
              unit="days"
              icon="flame"
              tone="warm"
            />
            <StatTile
              label="Takes"
              value={String(summary.takesCount)}
              icon="play"
              tone="ok"
            />
          </div>
        </section>

        <Card padding="lg">
          <MoodPicker currentMood={selectedMood} onSelect={handleMoodSelect} />
        </Card>

        <Card padding="lg">
          <label
            htmlFor="session-notes"
            className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--ink-3)]"
          >
            Notes
          </label>
          <textarea
            id="session-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            onBlur={handleNotesBlur}
            placeholder="What did you focus on today?"
            className={cn(
              'mt-3 w-full rounded-[var(--r-sm)] border border-[var(--rule)] bg-[var(--bg-soft)]',
              'p-4 text-sm text-[var(--ink)] placeholder:text-[var(--ink-3)]',
              'focus:border-[var(--brand)] focus:outline-none',
              'min-h-[100px]',
            )}
          />
        </Card>

        {tunesCount > 0 ? (
          <Card padding="lg">
            <Micro className="mb-4">Tunes Practiced</Micro>
            <ul className="mt-3 divide-y divide-[var(--rule)]">
              {summary.perTuneMasteryDelta.map((delta) => (
                <li
                  key={delta.userArrangementId}
                  className="flex items-center justify-between py-3 text-sm"
                >
                  <span className="font-medium text-[var(--ink)]">{delta.arrangementName}</span>
                  {delta.levelled && (
                    <span className="text-xs font-bold text-[var(--ok)]">Level up!</span>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        ) : (
          <Card padding="lg">
            <p className="text-sm text-[var(--ink-3)]">No tunes were assessed this session.</p>
          </Card>
        )}

        <div className="flex justify-center">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => void navigate({ to: '/practice' })}
          >
            Done
          </Button>
        </div>
      </main>
    </div>
  );
}
